import React, { useEffect, useState } from 'react';
import { Shield, AlertTriangle, Plus, Edit3, Trash2, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { AppButton } from '../components/base/AppButton';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import { AppSwitch } from '../components/base/AppSwitch';
import { Toast } from '../components/Toast';
import { PolicyEditModal, PolicyCreateModal, PolicyDeleteModal } from '../components/modals/PolicyModals';
import * as mockApi from '../services/mockApi';
import type { Policy } from '../types';
type ComplianceTab = 'ai-act' | 'gdpr';
interface PolicyPageProps {
  hideHeader?: boolean;
  hideTabs?: boolean;
  complianceTab?: ComplianceTab;
  serviceScope?: string | null;
}
export function PolicyPage({
  hideHeader = false,
  hideTabs = false,
  complianceTab,
  serviceScope
}: PolicyPageProps) {
  const [internalTab, setInternalTab] = useState<ComplianceTab>('ai-act');
  const activeTab = complianceTab ?? internalTab;
  const setActiveTab = (tab: ComplianceTab) => {
    if (!complianceTab) setInternalTab(tab);
  };
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  // Modal states
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState<Policy | null>(null);
  // Toast state
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Load policies
  useEffect(() => {
    loadPolicies();
  }, []);
  const loadPolicies = async () => {
    setIsLoading(true);
    try {
      const data = await mockApi.getPolicies();
      setPolicies(data);
    } catch (error) {
      setToast({
        message: 'Errore nel caricamento delle policy',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleToggle = async (policyId: string) => {
    const policy = policies.find(p => p.id === policyId);
    if (!policy) return;
    try {
      await mockApi.updatePolicy(policyId, {
        enabled: !policy.enabled
      });
      setPolicies(prev => prev.map(p => p.id === policyId ? {
        ...p,
        enabled: !p.enabled
      } : p));
      setToast({
        message: `Policy ${!policy.enabled ? 'attivata' : 'disattivata'}`,
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: "Errore nell'aggiornamento della policy",
        type: 'error'
      });
    }
  };
  const handleSaveEdit = async (id: string, data: Partial<Policy>) => {
    try {
      const updated = await mockApi.updatePolicy(id, data);
      setPolicies(prev => prev.map(p => p.id === id ? updated : p));
      setToast({
        message: 'Policy aggiornata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nel salvataggio',
        type: 'error'
      });
      throw error;
    }
  };
  const handleCreate = async (data: Omit<Policy, 'id'>) => {
    try {
      const newPolicy = await mockApi.createPolicy(data);
      setPolicies(prev => [...prev, newPolicy]);
      setToast({
        message: 'Policy creata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: 'Errore nella creazione',
        type: 'error'
      });
      throw error;
    }
  };
  const handleDelete = async (id: string) => {
    try {
      await mockApi.deletePolicy(id);
      setPolicies(prev => prev.filter(p => p.id !== id));
      setToast({
        message: 'Policy eliminata',
        type: 'success'
      });
    } catch (error) {
      setToast({
        message: "Errore nell'eliminazione",
        type: 'error'
      });
      throw error;
    }
  };
  const filteredPolicies = selectedCategory === 'all' ? policies : policies.filter(p => p.category === selectedCategory);
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AppBadge variant="red" data-id="element-2869">Alta</AppBadge>;
      case 'medium':
        return <AppBadge variant="amber" data-id="element-2870">Media</AppBadge>;
      case 'low':
        return <AppBadge variant="default" data-id="element-2871">Bassa</AppBadge>;
      default:
        return null;
    }
  };
  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      privacy: 'Privacy',
      content: 'Contenuti',
      behavior: 'Comportamento',
      compliance: 'Compliance'
    };
    return labels[category] || category;
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-20" data-id="element-2872">
        <p className="text-white/40" data-id="element-2873">Caricamento...</p>
      </div>;
  }
  return <div className="pb-12 space-y-8" data-id="element-2874">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-2875" />}

      {/* Header */}
      {!hideHeader && <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-2876">
          <div data-id="element-2877">
            <h1 className="text-[20px] font-semibold text-white tracking-tight" data-id="element-2878">
              Compliance & Policy
            </h1>
            <p className="text-[13px] text-white/40 mt-1" data-id="element-2879">
              Gestisci policy aziendali e conformità normativa
            </p>
          </div>
        </div>}

      {/* Tabs */}
      {!hideTabs && <div className="border-b border-white/[0.06]" data-id="element-2880">
          <div className="flex space-x-1" data-id="element-2881">
            <button onClick={() => setActiveTab('ai-act')} className={`flex items-center px-4 py-3 text-[12px] font-medium transition-all relative ${activeTab === 'ai-act' ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-2882">
              <AlertCircle className="w-4 h-4 mr-2" data-id="element-2883" />
              AI ACT
              {activeTab === 'ai-act' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2884" />}
            </button>
            <button onClick={() => setActiveTab('gdpr')} className={`flex items-center px-4 py-3 text-[12px] font-medium transition-all relative ${activeTab === 'gdpr' ? 'text-white' : 'text-white/35 hover:text-white/60'}`} data-id="element-2885">
              <Shield className="w-4 h-4 mr-2" data-id="element-2886" />
              GDPR
              {activeTab === 'gdpr' && <div className="absolute bottom-0 left-2 right-2 h-[2px] bg-[#8eff01] rounded-full" data-id="element-2887" />}
            </button>
          </div>
        </div>}

      {/* Tab Content */}
      {activeTab === 'ai-act' && <div className="space-y-6" data-id="element-2888">
          {/* AI ACT Compliance Analysis */}
          <AppCard className="bg-white/[0.02] border-white/[0.08]" data-id="element-2889">
            <div className="flex items-start justify-between mb-6" data-id="element-2890">
              <div data-id="element-2891">
                <h2 className="text-[17px] font-semibold text-white mb-2" data-id="element-2892">
                  Analisi Conformità AI ACT
                </h2>
                <p className="text-[12px] text-white/40" data-id="element-2893">
                  Valutazione automatica del livello di rischio e conformità
                  alla normativa europea
                </p>
              </div>
              <AppBadge variant="green" data-id="element-2894">Analisi Attiva</AppBadge>
            </div>

            {/* Risk Level */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-2895">
              <AppCard className="bg-white/[0.03] border-white/[0.08]" data-id="element-2896">
                <div className="text-center" data-id="element-2897">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-500/5 mb-3" data-id="element-2898">
                    <AlertTriangle className="w-5 h-5 text-amber-400" data-id="element-2899" />
                  </div>
                  <h3 className="text-[12px] font-semibold text-white mb-1" data-id="element-2900">
                    Livello di Rischio
                  </h3>
                  <p className="text-[20px] font-bold text-amber-400 mb-1" data-id="element-2901">
                    Medio
                  </p>
                  <p className="text-[10px] text-white/40" data-id="element-2902">
                    Richiede monitoraggio
                  </p>
                </div>
              </AppCard>

              <AppCard className="bg-white/[0.03] border-white/[0.08]" data-id="element-2903">
                <div className="text-center" data-id="element-2904">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#8eff01]/10 mb-3" data-id="element-2905">
                    <CheckCircle className="w-5 h-5 text-[#8eff01]" data-id="element-2906" />
                  </div>
                  <h3 className="text-[12px] font-semibold text-white mb-1" data-id="element-2907">
                    Score Compliance
                  </h3>
                  <p className="text-[20px] font-bold text-[#8eff01] mb-1" data-id="element-2908">
                    78%
                  </p>
                  <p className="text-[10px] text-white/40" data-id="element-2909">Buono</p>
                </div>
              </AppCard>

              <AppCard className="bg-white/[0.03] border-white/[0.08]" data-id="element-2910">
                <div className="text-center" data-id="element-2911">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/[0.06] mb-3" data-id="element-2912">
                    <Shield className="w-5 h-5 text-white/40" data-id="element-2913" />
                  </div>
                  <h3 className="text-[12px] font-semibold text-white mb-1" data-id="element-2914">
                    Categoria AI ACT
                  </h3>
                  <p className="text-[15px] font-bold text-white mb-1" data-id="element-2915">
                    Limited Risk
                  </p>
                  <p className="text-[10px] text-white/40" data-id="element-2916">
                    Obblighi di trasparenza
                  </p>
                </div>
              </AppCard>
            </div>

            {/* Compliance Progress */}
            <div className="mb-6" data-id="element-2917">
              <div className="flex items-center justify-between mb-3" data-id="element-2918">
                <h3 className="text-[12px] font-semibold text-white/70" data-id="element-2919">
                  Conformità Complessiva
                </h3>
                <span className="text-[12px] font-medium text-white" data-id="element-2920">
                  78/100
                </span>
              </div>
              <div className="h-3 bg-white/[0.03] rounded-full overflow-hidden" data-id="element-2921">
                <div className="h-full bg-[#8eff01] transition-all duration-500" style={{
              width: '78%'
            }} data-id="element-2922" />
              </div>
            </div>

            {/* Requirements Checklist */}
            <div className="space-y-3" data-id="element-2923">
              <h3 className="text-[12px] font-semibold text-white/70 mb-4" data-id="element-2924">
                Requisiti AI ACT
              </h3>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-2925">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-2926" />
                <div className="flex-1" data-id="element-2927">
                  <p className="text-[12px] font-medium text-white" data-id="element-2928">
                    Trasparenza e Informazione
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-2929">
                    Gli utenti sono informati dell'interazione con un sistema AI
                  </p>
                </div>
                <AppBadge variant="green" data-id="element-2930">Conforme</AppBadge>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-2931">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-2932" />
                <div className="flex-1" data-id="element-2933">
                  <p className="text-[12px] font-medium text-white" data-id="element-2934">
                    Documentazione Tecnica
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-2935">
                    Documentazione completa del sistema e dei processi
                    decisionali
                  </p>
                </div>
                <AppBadge variant="green" data-id="element-2936">Conforme</AppBadge>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-amber-500/20" data-id="element-2937">
                <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-2938" />
                <div className="flex-1" data-id="element-2939">
                  <p className="text-[12px] font-medium text-white" data-id="element-2940">
                    Supervisione Umana
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-2941">
                    Necessario implementare meccanismi di supervisione più
                    robusti
                  </p>
                </div>
                <AppBadge variant="amber" data-id="element-2942">In Progress</AppBadge>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-2943">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-2944" />
                <div className="flex-1" data-id="element-2945">
                  <p className="text-[12px] font-medium text-white" data-id="element-2946">
                    Gestione dei Dati
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-2947">
                    Qualità dei dati e governance conforme ai requisiti
                  </p>
                </div>
                <AppBadge variant="green" data-id="element-2948">Conforme</AppBadge>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-red-500/20" data-id="element-2949">
                <XCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" data-id="element-2950" />
                <div className="flex-1" data-id="element-2951">
                  <p className="text-[12px] font-medium text-white" data-id="element-2952">
                    Registro delle Attività
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-2953">
                    Implementare logging dettagliato delle decisioni AI
                  </p>
                </div>
                <AppBadge variant="red" data-id="element-2954">Non Conforme</AppBadge>
              </div>
            </div>
          </AppCard>

          {/* Recommendations */}
          <AppCard data-id="element-2955">
            <h3 className="text-[15px] font-semibold text-white mb-4" data-id="element-2956">
              Raccomandazioni
            </h3>
            <div className="space-y-3" data-id="element-2957">
              <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-id="element-2958">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-2959" />
                <div data-id="element-2960">
                  <p className="text-[12px] font-medium text-white mb-1" data-id="element-2961">
                    Priorità Alta
                  </p>
                  <p className="text-[12px] text-white/40" data-id="element-2962">
                    Implementare un sistema di logging completo per tracciare
                    tutte le decisioni automatizzate del sistema AI
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl" data-id="element-2963">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" data-id="element-2964" />
                <div data-id="element-2965">
                  <p className="text-[12px] font-medium text-white mb-1" data-id="element-2966">
                    Priorità Media
                  </p>
                  <p className="text-[12px] text-white/40" data-id="element-2967">
                    Rafforzare i meccanismi di supervisione umana con dashboard
                    di monitoraggio in tempo reale
                  </p>
                </div>
              </div>
            </div>
          </AppCard>
        </div>}

      {activeTab === 'gdpr' && <div className="space-y-6" data-id="element-2968">
          <AppCard data-id="element-2969">
            <h2 className="text-[17px] font-semibold text-white mb-4" data-id="element-2970">
              Conformità GDPR
            </h2>
            <p className="text-white/40 text-[12px] mb-6" data-id="element-2971">
              Gestione della conformità al Regolamento Generale sulla Protezione
              dei Dati
            </p>

            {/* GDPR Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-2972">
              <div className="p-5 bg-white/[0.02] rounded-xl border border-white/[0.06]" data-id="element-2973">
                <div className="flex items-center gap-3 mb-2" data-id="element-2974">
                  <CheckCircle className="w-5 h-5 text-[#8eff01]" data-id="element-2975" />
                  <h3 className="text-[12px] font-semibold text-white/70" data-id="element-2976">
                    Consenso Utenti
                  </h3>
                </div>
                <p className="text-[24px] font-semibold text-white" data-id="element-2977">100%</p>
                <p className="text-[11px] text-white/30 mt-1 uppercase tracking-wider" data-id="element-2978">
                  Tracciato e documentato
                </p>
              </div>

              <div className="p-5 bg-white/[0.02] rounded-xl border border-white/[0.06]" data-id="element-2979">
                <div className="flex items-center gap-3 mb-2" data-id="element-2980">
                  <CheckCircle className="w-5 h-5 text-[#8eff01]" data-id="element-2981" />
                  <h3 className="text-[12px] font-semibold text-white/70" data-id="element-2982">
                    Data Retention
                  </h3>
                </div>
                <p className="text-[24px] font-semibold text-white" data-id="element-2983">Conforme</p>
                <p className="text-[11px] text-white/30 mt-1 uppercase tracking-wider" data-id="element-2984">
                  Policy attive
                </p>
              </div>

              <div className="p-5 bg-white/[0.02] rounded-xl border border-white/[0.06]" data-id="element-2985">
                <div className="flex items-center gap-3 mb-2" data-id="element-2986">
                  <Shield className="w-5 h-5 text-white/40" data-id="element-2987" />
                  <h3 className="text-[12px] font-semibold text-white/70" data-id="element-2988">
                    Diritti Utenti
                  </h3>
                </div>
                <p className="text-[24px] font-semibold text-white" data-id="element-2989">Attivi</p>
                <p className="text-[11px] text-white/30 mt-1 uppercase tracking-wider" data-id="element-2990">
                  Accesso, cancellazione, portabilità
                </p>
              </div>
            </div>

            {/* GDPR Principles */}
            <div className="space-y-3" data-id="element-2991">
              <h3 className="text-[12px] font-semibold text-white/70 mb-4" data-id="element-2992">
                Principi GDPR
              </h3>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-2993">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-2994" />
                <div className="flex-1" data-id="element-2995">
                  <p className="text-[12px] font-medium text-white" data-id="element-2996">
                    Liceità, correttezza e trasparenza
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-2997">
                    Trattamento dati conforme e trasparente
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-2998">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-2999" />
                <div className="flex-1" data-id="element-3000">
                  <p className="text-[12px] font-medium text-white" data-id="element-3001">
                    Limitazione della finalità
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-3002">
                    Dati raccolti per scopi specifici e legittimi
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-3003">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-3004" />
                <div className="flex-1" data-id="element-3005">
                  <p className="text-[12px] font-medium text-white" data-id="element-3006">
                    Minimizzazione dei dati
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-3007">
                    Solo dati necessari vengono raccolti
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-3008">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-3009" />
                <div className="flex-1" data-id="element-3010">
                  <p className="text-[12px] font-medium text-white" data-id="element-3011">
                    Esattezza
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-3012">
                    Meccanismi di aggiornamento e correzione attivi
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-3013">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-3014" />
                <div className="flex-1" data-id="element-3015">
                  <p className="text-[12px] font-medium text-white" data-id="element-3016">
                    Limitazione della conservazione
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-3017">
                    Policy di retention automatiche implementate
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-white/[0.03] rounded-lg border border-white/[0.08]" data-id="element-3018">
                <CheckCircle className="w-5 h-5 text-[#8eff01] flex-shrink-0 mt-0.5" data-id="element-3019" />
                <div className="flex-1" data-id="element-3020">
                  <p className="text-[12px] font-medium text-white" data-id="element-3021">
                    Integrità e riservatezza
                  </p>
                  <p className="text-[10px] text-white/40 mt-1" data-id="element-3022">
                    Crittografia e misure di sicurezza attive
                  </p>
                </div>
              </div>
            </div>
          </AppCard>
        </div>}

      {/* Modals */}
      <PolicyEditModal isOpen={!!editingPolicy} policy={editingPolicy} onClose={() => setEditingPolicy(null)} onSave={handleSaveEdit} data-id="element-3023" />

      <PolicyCreateModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onCreate={handleCreate} data-id="element-3024" />

      <PolicyDeleteModal isOpen={!!deletingPolicy} policy={deletingPolicy} onClose={() => setDeletingPolicy(null)} onDelete={handleDelete} data-id="element-3025" />
    </div>;
}